import React, { FC, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  FormControl,
} from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import { SET_USER } from "slices/global.slice";
import profilService from "services/profilService";
import userService from "services/userService";
import { getImageUrl, readFileDataAsBase64 } from "utils/imageUtils";
import Genders from "models/Enums/Genders";
import User from "models/User";
import classes from "./style";

const PasseportParametres: FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((s: RootState) => s.globalSlice);

  const [imgUrl, setImgUrl] = useState<string | undefined>(user.profilePicture);

  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? "");
  const [gender, setGender] = useState(user.gender);
  const [birthDay, setBirthDay] = useState<string>(() => {
    const d = user.birthDay ? new Date(user.birthDay) : null;
    if (!d || Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [email, setEmail] = useState(user.email ?? "");

  const [password, setPassword] = useState<string>("");
  const [confirmation, setConfirmation] = useState<string>("");
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const [snackMessage, setSnack] = useState<string | undefined>(undefined);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const updateUser = (u: User) => dispatch(SET_USER(u));

  const onAvatarClick = () => fileInputRef.current?.click();

  const onAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = (await readFileDataAsBase64(file)) as string;
    const r = await profilService.updateProfilPicture(base64);
    setImgUrl(r);
    updateUser({ ...user, profilePicture: r });
  };

  const savePassword = async () => {
    await userService.updatePassword(password, confirmation);
    setPassword("");
    setConfirmation("");
    setSnack("Votre mot de passe a été changé.");
  };

  const save = async () => {
    await profilService.updateProfil(
      firstName,
      lastName,
      birthDay ? new Date(birthDay) : new Date(),
      email,
      phoneNumber,
      gender
    );
    updateUser({
      ...user,
      firstName,
      lastName,
      phoneNumber,
      birthDay,
      email,
      gender,
    } as User);
    setSnack("Vos informations ont été mises à jour.");
  };

  return (
    <>
      <Container disableGutters sx={[classes.emptyPasseport, classes.parametres]}>
        <Typography variant="h1" my={4}>
          PARAMÈTRES
        </Typography>

        <Grid container sx={classes.messagerie} my={3} spacing={2}>
          <Grid item xs={12} md={2}>
            <Box sx={{ position: "relative", width: "fit-content" }} onClick={onAvatarClick}>
              <Avatar
                sx={classes.profileAvatar}
                alt={`${user.firstName} ${user.lastName}`}
                src={getImageUrl(imgUrl)}
              />
              <Box sx={classes.uploadAvatar}>
                <AddAPhotoIcon />
              </Box>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={onAvatarChange}
            />
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ p: 2 }}>
              <Typography component="div" variant="h5" mb={1}>
                Paramètres
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <TextField
                  label="Prénom"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  sx={{ my: 1 }}
                />

                <TextField
                  label="Nom"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  sx={{ my: 1 }}
                />

                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ my: 1 }}
                  type="email"
                  autoComplete="email"
                  fullWidth
                />

                <FormControl sx={{ my: 1 }}>
                  <InputLabel id="gender-label">Sexe</InputLabel>
                  <Select
                    labelId="gender-label"
                    label="Sexe"
                    value={Genders[gender]}
                    onChange={(e) => setGender(Genders[e.target.value as keyof typeof Genders] as any)}
                  >
                    <MenuItem value={"Female"}>Femme</MenuItem>
                    <MenuItem value={"Male"}>Homme</MenuItem>
                    <MenuItem value={"Other"}>Autre</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Date de naissance"
                  value={birthDay}
                  type="date"
                  onChange={(e) => setBirthDay(e.target.value)}
                  sx={{ my: 1 }}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Téléphone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  sx={{ my: 1 }}
                  inputMode="tel"
                  autoComplete="tel"
                />

                <Box display="flex" justifyContent="end" mt={2}>
                  <Button variant="contained" color="primary" onClick={save}>
                    Enregistrer
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ p: 2 }}>
              <Typography component="div" variant="h5" mb={1}>
                Mot de passe
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <TextField
                  label="Mot de passe"
                  value={password}
                  type={showPw1 ? "text" : "password"}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ my: 1 }}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Afficher/masquer le mot de passe"
                          onClick={() => setShowPw1((v) => !v)}
                          edge="end"
                        >
                          {showPw1 ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirmation du mot de passe"
                  value={confirmation}
                  type={showPw2 ? "text" : "password"}
                  onChange={(e) => setConfirmation(e.target.value)}
                  sx={{ my: 1 }}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Afficher/masquer le mot de passe"
                          onClick={() => setShowPw2((v) => !v)}
                          edge="end"
                        >
                          {showPw2 ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box display="flex" justifyContent="end" mt={2}>
                  <Button variant="contained" color="primary" onClick={savePassword}>
                    Enregistrer
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={!!snackMessage}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnack(undefined)}
        message={snackMessage}
      />
    </>
  );
};

export default PasseportParametres;
